// src/serviceWorkerRegistration.js
// Service Worker registration module for Tonic Suite (CRA 5 convention)

import logger from './shared/services/logging/logger';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/
  )
);

// How long after page load to ignore SW controllerchange events (ms).
// Prevents an immediate reload if the SW activates before the app has settled.
const SW_SETTLE_MS = 2000;
const pageLoadTime = Date.now();

// Set up reload listener at module scope — MUST happen before any SW
// registration so we never miss a controllerchange event if the new SW
// activates very quickly (e.g. via skipWaiting + clientsClaim).
//
// The page only reloads AFTER the new SW takes control, and the new SW only
// takes control once SKIP_WAITING is posted — which now happens only when the
// user clicks "Update Now" in the update notification. So we never force a
// reload mid-action (e.g. while a script is running or saving).
let refreshing = false;
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    if (Date.now() - pageLoadTime < SW_SETTLE_MS) return;
    refreshing = true;
    window.location.reload();
  });
}

/**
 * Notify the app that a new version is ready. The ServiceWorkerUpdateNotification
 * component listens for this and shows a "Update Now / Later" bar, so the user
 * chooses when to reload instead of being interrupted by an automatic refresh.
 */
function notifyUpdateAvailable(registration) {
  window.dispatchEvent(new CustomEvent('swUpdateAvailable', { detail: registration }));
}

/**
 * Register the service worker in production.
 * @param {Object} config
 * @param {Function} config.onSuccess - Called when content is cached for offline use
 * @param {Function} config.onUpdate  - Called when new content is available
 */
export function register(config) {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    // SW won't work if PUBLIC_URL is on a different origin
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      // On localhost, check if a SW still exists before registering
      checkValidServiceWorker(swUrl, config);

      // Fire-and-forget: wait for service worker to be ready
      (async () => {
        try {
          await navigator.serviceWorker.ready;
          logger.info('Service worker is active on localhost');
        } catch (error) {
          logger.error('Service worker ready failed', { error: error.message });
        }
      })();
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

async function registerValidSW(swUrl, config) {
  try {
    const registration = await navigator.serviceWorker.register(swUrl);
    logger.info('Service worker registered', { scope: registration.scope });

    // If a SW is already waiting (from a previous visit), let the user decide
    // when to activate it rather than reloading the page out from under them.
    if (registration.waiting) {
      logger.info('Found waiting service worker, prompting user to update');
      notifyUpdateAvailable(registration);
    }

    // Force an update check right now (don't wait an hour)
    registration.update();

    // Also check for updates every hour
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      if (installingWorker == null) return;

      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New content available — prompt the user instead of force-reloading.
            // SKIP_WAITING (and the subsequent reload) only fires when the user
            // clicks "Update Now" in the notification bar.
            logger.info('New app version available, prompting user to update');
            notifyUpdateAvailable(registration);
            if (config && config.onUpdate) {
              config.onUpdate(registration);
            }
          } else {
            // Content cached for the first time (offline-ready)
            logger.info('Content is cached for offline use');
            if (config && config.onSuccess) {
              config.onSuccess(registration);
            }
          }
        }
      };
    };
  } catch (error) {
    logger.error('Error during service worker registration', {
      error: error.message,
    });
  }
}

async function checkValidServiceWorker(swUrl, config) {
  try {
    const response = await fetch(swUrl, { headers: { 'Service-Worker': 'script' } });
    const contentType = response.headers.get('content-type');
    if (
      response.status === 404 ||
      (contentType != null && contentType.indexOf('javascript') === -1)
    ) {
      // No SW found — unregister any existing one and reload
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.unregister();
        window.location.reload();
      } catch (error) {
        logger.error('Failed to unregister service worker', { error: error.message });
      }
    } else {
      await registerValidSW(swUrl, config);
    }
  } catch (error) {
    logger.warn('No internet connection. App is running in offline mode.');
  }
}

export async function unregister() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.unregister();
      logger.info('Service worker unregistered');
    } catch (error) {
      logger.error('Error unregistering service worker', {
        error: error.message,
      });
    }
  }
}
